// src/receptionist/screens/Merchants.tsx
//
// The ourteam-staff roll-up: every merchant, where each one is in onboarding,
// and how its queue looks — the screen staff land on and drill in from
// (ourteam-frontend#3611, BE ourteam-backend#4670). Structured like
// @oura/screens/Businesses: header, search, table, pagination, empty state.
//
// Two rules that are the backend's, not ours:
//   • Rows render in the ORDER THE SERVER RETURNED THEM. The API exposes page,
//     limit and search — no sort — so re-sorting one page of an arbitrary set
//     would put "who needs attention" on screen and mean nothing. If ordering
//     is wanted it is a `sort` parameter on #4670, raised there.
//   • `onboardingStage` is derived server-side (first checklist item not done,
//     or "live"). Rendered, never recomputed.

import { useCallback, useEffect, useRef, useState } from "react";
import { Building2Icon, SearchIcon, ShieldOffIcon, AlertTriangleIcon, Loader2Icon } from "lucide-react";
import { PageHeader, Card, EmptyState, Button } from "../components/kit";
import { Pagination } from "@oura/primitives/Pagination";
import { TagPill } from "../components/ui";
import { Input } from "@component/input";
import { cn } from "@oura/lib/cn";
import { listMerchants, type MerchantSummary, type MerchantsPage, type OnboardingStage } from "@service/receptionist";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

/** The six stages, in the order the backend derives them. */
const STAGE_LABEL: Record<OnboardingStage, string> = {
    details: "Business details",
    meta_verification: "Meta verification",
    number_migration: "Moving the number",
    templates_pending: "Templates pending",
    kb_signoff: "Awaiting sign-off",
    live: "Live",
};

const CATEGORY_LABEL: Record<string, string> = {
    clinic: "Skin or aesthetic clinic",
    salon: "Hair salon",
    spa: "Spa or wellness",
    dental: "Dental clinic",
};

/**
 * Only `live` is green. Every other stage is amber, because every other stage
 * means the merchant is not answering customers yet. An unrecognised value
 * renders as the raw string — a newer backend adding a stage must not crash
 * the page, and hiding it would hide a merchant.
 */
function StageChip({ stage }: { stage: OnboardingStage | string }) {
    const label = (STAGE_LABEL as Record<string, string>)[stage] ?? stage;
    return (
        <TagPill tone={stage === "live" ? "green" : "amber"}>{label}</TagPill>
    );
}

function lastActivity(iso: string | null): string {
    if (!iso) return "No messages yet";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/** The 403 the API sends a merchant token on this route — not a transport failure. */
function isForbidden(err: unknown): boolean {
    const status = (err as { response?: { status?: number } } | null)?.response?.status;
    return status === 403;
}

type Load =
    | { kind: "loading" }
    | { kind: "forbidden" }
    | { kind: "error" }
    | { kind: "ready"; page: MerchantsPage };

export function MerchantsScreen({ onSelectMerchant }: { onSelectMerchant: (merchantId: string) => void }) {
    const [query, setQuery] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [state, setState] = useState<Load>({ kind: "loading" });
    // Bumped on every request so a slow response for an earlier query cannot
    // land after a faster one for the current query.
    const seq = useRef(0);

    // The search box calls the API, not a local filter: the list is paginated,
    // so a local filter would only ever search the page on screen.
    useEffect(() => {
        const t = window.setTimeout(() => {
            setSearch(query.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [query]);

    const load = useCallback(async () => {
        const mine = ++seq.current;
        setState((cur) => (cur.kind === "ready" ? cur : { kind: "loading" }));
        try {
            const result = await listMerchants({ page, limit: PAGE_SIZE, search: search || undefined });
            if (seq.current !== mine) return;
            setState({ kind: "ready", page: result });
        } catch (err) {
            if (seq.current !== mine) return;
            setState({ kind: isForbidden(err) ? "forbidden" : "error" });
        }
    }, [page, search]);

    useEffect(() => { void load(); }, [load]);

    return (
        <div>
            <PageHeader
                title="Merchants"
                subtitle="Every business on the receptionist, where each one is in onboarding, and how its queue looks. Open a row to work in that merchant's console."
            />

            <div className="mb-4 flex items-center gap-3">
                <div className="relative w-full max-w-sm">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name"
                        aria-label="Search merchants by name"
                        className="pl-9"
                    />
                </div>
                {state.kind === "ready" && (
                    <span className="text-xs text-gray-500 tabular-nums">
                        {state.page.pagination.total} {state.page.pagination.total === 1 ? "merchant" : "merchants"}
                    </span>
                )}
            </div>

            <Card className="overflow-hidden">
                {state.kind === "forbidden" ? (
                    <EmptyState
                        icon={ShieldOffIcon}
                        title="This session cannot see the merchant list"
                        description="The roll-up is for ourteam staff. A merchant account sees its own console instead — open Bookings from the rail."
                    />
                ) : state.kind === "error" ? (
                    <EmptyState
                        icon={AlertTriangleIcon}
                        title="Couldn't load the merchants"
                        description="Something went wrong asking for the list."
                        action={<Button onClick={() => void load()}>Try again</Button>}
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-max">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="h-[45px] px-4 py-3 text-left text-[13px] leading-[18px] font-medium text-gray-500">Merchant</th>
                                    <th className="h-[45px] whitespace-nowrap px-3 py-3 text-left text-[13px] leading-[18px] font-medium text-gray-500">Category</th>
                                    <th className="h-[45px] whitespace-nowrap px-3 py-3 text-left text-[13px] leading-[18px] font-medium text-gray-500">Onboarding</th>
                                    <th className="h-[45px] whitespace-nowrap px-3 py-3 text-center text-[13px] leading-[18px] font-medium text-gray-500">Pending</th>
                                    <th className="h-[45px] whitespace-nowrap px-3 py-3 text-center text-[13px] leading-[18px] font-medium text-gray-500">Bookings · 7d</th>
                                    <th className="h-[45px] whitespace-nowrap px-3 py-3 text-center text-[13px] leading-[18px] font-medium text-gray-500">Escalations · 7d</th>
                                    <th className="h-[45px] whitespace-nowrap px-3 py-3 text-left text-[13px] leading-[18px] font-medium text-gray-500">Last message</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {state.kind === "loading" ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-16 text-center">
                                            <Loader2Icon className="mx-auto h-5 w-5 animate-spin text-gray-400" />
                                        </td>
                                    </tr>
                                ) : state.page.merchants.length === 0 ? (
                                    <tr>
                                        <td colSpan={7}>
                                            <EmptyState
                                                icon={Building2Icon}
                                                title={search ? `No merchant matches "${search}"` : "No merchants yet"}
                                                description={search ? "Try a shorter part of the name." : "Merchants appear here once Super Admin provisions a Receptionist workspace."}
                                            />
                                        </td>
                                    </tr>
                                ) : (
                                    state.page.merchants.map((m: MerchantSummary) => (
                                        <tr
                                            key={m.id}
                                            className="group cursor-pointer transition-colors hover:bg-gray-50"
                                            onClick={() => onSelectMerchant(m.id)}
                                        >
                                            <td className="px-4 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#E8F1FF] text-[#0A5FD1]">
                                                        <Building2Icon className="h-4 w-4 text-blue-600" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium text-gray-900 group-hover:text-blue-700">{m.name}</div>
                                                        <div className="text-xs text-gray-500">{m.outlet || "—"}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3.5 text-sm text-gray-700">{CATEGORY_LABEL[m.category] ?? m.category}</td>
                                            <td className="px-3 py-3.5"><StageChip stage={m.onboardingStage} /></td>
                                            <td className={cn("px-3 py-3.5 text-center text-sm tabular-nums", m.pendingBookings > 0 ? "font-semibold text-amber-700" : "text-gray-500")}>{m.pendingBookings}</td>
                                            <td className="px-3 py-3.5 text-center text-sm tabular-nums text-gray-700">{m.bookingsLast7Days}</td>
                                            <td className={cn("px-3 py-3.5 text-center text-sm tabular-nums", m.escalationsLast7Days > 0 ? "text-rose-700" : "text-gray-500")}>{m.escalationsLast7Days}</td>
                                            <td className={cn("px-3 py-3.5 text-sm whitespace-nowrap", m.lastActivityAt ? "text-gray-700" : "text-gray-400")}>{lastActivity(m.lastActivityAt)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        {state.kind === "ready" && (
                            <Pagination
                                page={state.page.pagination.page}
                                total={state.page.pagination.total}
                                pageSize={state.page.pagination.limit}
                                itemLabel="merchants"
                                onPageChange={setPage}
                                className="border-t border-gray-200 px-4 py-3"
                            />
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
